<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
        {{ item?.id ? $t('cashboxEditModalTitle') : $t('cashboxCreateModalTitle') }}
      </div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="cashbox_name" :class="{'p-invalid' : submitted && !cashboxData.name}" v-model="cashboxData.name" autocomplete="off"/>
          <label for="cashbox_name">{{ $t('Name') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !cashboxData.name">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="cashbox_description" v-model="cashboxData.description" autocomplete="off"/>
          <label for="cashbox_description">{{ $t('Description') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <div class="p-float-label">
          <MultiSelect @show="toggleDropdownOverlayVisibility(true)"
                       @hide="toggleDropdownOverlayVisibility(false)"
                       id="cashbox-modal-users"
                       ref="selectedUsersDropdown"
                       v-model="selectedUsers"
                       :options="usersData"
                       optionLabel="full_name"
                       :filter="true"
                       class="ms-multiline"
                       display="chip"/>
          <label for="cashbox-modal-users">{{ $t('Access') }}</label>
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import settings from "@/settings";
import overlayVisibilityMixins from "@/mixins/overlayVisibilityMixins";

export default {
  mixins: [ httpMixins, overlayVisibilityMixins ],
  emits: ['close', 'update-items'],
  name: 'CashboxModal',
  props: {
    item: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    cashboxUsers: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      dataIsSending: false,
      disableSaveButton: false,
      usersData: [],
      cashboxData: {},
      submitted: false,
      selectedUsers: null,
    }
  },
  watch: {
    '$store.state.dropdownOverlayVisible'(value) {
      if (!value) {
        this.$refs.selectedUsersDropdown?.hide()
      }
    },
    item(value) {
      if (!value) {
        this.cashboxData = {}
        this.selectedUsers = []
        return
      }

      this.cashboxData = { ...value }

      if (value.users?.length) {
        const sortedUsers = [...value.users]
            .map(m => ({ ...m, id: m.user?.id ? +m.user?.id : 0 }))  // нормализуем id в masters
            .filter(m => m.id > 0)
            .sort((a, b) => a.id - b.id);

        this.selectedUsers = sortedUsers.map(user => {
          const id = user.id;
          const full_name = `${user.user?.first_name ?? ''} ${user.user?.last_name ?? ''}`.trim();

          const exists = this.usersData.find(m => +m.id === id);
          const userData = { id, full_name };

          if (!exists) this.usersData.push(userData);

          return userData;
        });
      } else {
        this.selectedUsers = []
      }
    },
    visible(value) {
      if (!value) {
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.selectedUsers = []
      }
    },
    cashboxUsers(value) {
      this.usersData = Array.isArray(value) ? [...value] : []
    },
  },
  methods: {
    async saveItem() {
      this.submitted = true
      if (!this.cashboxData.name) {
        return false
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      const cashbox = {
        id: this.cashboxData.id ?? null,
        name: this.cashboxData.name ?? null,
        description: this.cashboxData.description ?? null,
        employees: this.selectedUsers?.length ? this.selectedUsers.map(employee => employee.id) : null
      }

      if (this.cashboxData.id) {
        try {
          const { status, data } = await httpClient.post(`cashbox/update`, cashbox)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.cashboxData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else if (data?.error) {
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`cashbox/create`, cashbox)
          if (status === 201 && data?.success) {
            this.cashboxData.id = data.id
            cashbox.id = data.id
            this.$emit('update-items', this.cashboxData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife});
            this.close()
          } else if (data?.error) {
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '460px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '460px', marginTop: '25px'}
      } else {
        return {position: 'absolute', top: 0, width: '460px', marginTop: '50px'}
      }
    },
  },

}
</script>